<script setup lang="ts">
const { t: $t } = useI18n()

import type { ApiInputOfferPositionUpdateStatus, ApiInputPublicTransportationCompanyDriveUpdateLogisticsTaskStatus, ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid } from '~/types'
import { LogisticsTaskStatus } from '~/prisma/enums'
import { publicUpdateLogisticsTaskStatusSchema } from '~/server/schemas'

const props = defineProps<{
  logisticsTaskCuid: string
}>()
const emit = defineEmits<{
  (e: 'close'): void
}>()
const { publicTransportationCompanyDrive: queryPublicTransportationCompanyDrive } = useQuery()

const logisticsTaskCuid = computed(() => props.logisticsTaskCuid)
const { data: logisticsTask } = queryPublicTransportationCompanyDrive.getLogisticsTaskByCuid(logisticsTaskCuid)

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const update = useMutation({
  mutationFn: ({ positions, task }: { positions: ApiInputOfferPositionUpdateStatus, task: ApiInputPublicTransportationCompanyDriveUpdateLogisticsTaskStatus }) => {
    return $trpc.publicTransportationCompanyDrive.updateLogisticsTaskStatus.mutate({ ...task, positions })
  },
  onError: makeTrpcErrorToast(notification, { description: $t('logisticsTask.loadingChecklist.update.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['logisticsTask'] }),
      queryClient.invalidateQueries({ queryKey: ['offer'] }),
    ])
    emit('close')

    notification.success({ title: $t('logisticsTask.loadingChecklist.update.success'), duration: 4500 })
  },
})

function updateIsLoaded(task: ApiPublicTransportationCompanyDriveGetLogisticsTaskByCuid) {
  const positions = task.positionsToShip.map(p => ({ id: p.id, isLoaded: p.isLoaded }))
  const areNoPositionsLoaded = task.positionsToShip.every(p => !p.isLoaded)
  const taskPayload = {
    cuid: task.cuid,
    type: task.type,
    status: areNoPositionsLoaded ? LogisticsTaskStatus.created : LogisticsTaskStatus.loaded,
    disableAutomaticEmail: false,
    ...((task.type === 'outbound' && task.doesTransportationCompanyDoTask) && {
      transportationCompanyDriverName: task.transportationCompanyDriverName,
      transportationCompanyDriverSignature: task.transportationCompanyDriverSignature,
      transportationCompanyLicensePlateNumber: task.transportationCompanyLicensePlateNumber,
    }),
  }
  return update.mutate({
    positions,
    task: publicUpdateLogisticsTaskStatusSchema.parse(taskPayload),
  })
}
</script>

<template>
  <ThePopup show :title="$t('logisticsTask.loadingChecklist.title')" @close="emit('close')">
    <DriverLoadingChecklistForm
      v-if="logisticsTask"
      :logistics-task="logisticsTask"
      :disabled="update.isPending.value"
      @save="updateIsLoaded"
    />
  </ThePopup>
</template>
